<script setup lang="ts">
import { ref, shallowRef, watch, onBeforeUnmount, nextTick, computed } from 'vue';
import { Minus } from '@element-plus/icons-vue';
import * as am5 from '@amcharts/amcharts5';
import * as am5map from '@amcharts/amcharts5/map';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { ElTree } from 'element-plus';
import {
  UK_LOW_GEO_WITHOUT_REPUBLIC_OF_IRELAND,
  UK_MAP_VIEW_GEO_BOUNDS,
  buildAggregatedMapData,
  isGwpColumn,
} from '@/utilities/comparisonRegionMapGeo';
import { comparisonTableValueMapPickerOptionsFromTree } from '@/utilities/flattenComparisonColumnsTree';
import { decimalToPercentage, numberWithCommas } from '@/utilities/numberFormatter';
import { useDashboardStore } from '@/stores/dashboard';
import { usePortfolioStore } from '@/stores/portfolio';
import '@/assets/css/sidepanel.css';

const props = defineProps<{
  rows: Record<string, unknown>[] | null;
  tableValuesTree: unknown;
}>();

const dashboard_store = useDashboardStore();
const portfolio_store = usePortfolioStore();

const leftMetric = ref('');
const rightMetric = ref('');
const leftMapEl = ref<HTMLDivElement | null>(null);
const rightMapEl = ref<HTMLDivElement | null>(null);
const treeLeftRef = ref<InstanceType<typeof ElTree>>();
const treeRightRef = ref<InstanceType<typeof ElTree>>();

const tableValueOptions = computed(() => comparisonTableValueMapPickerOptionsFromTree(props.tableValuesTree));
const leafValueSet = computed(() => new Set(tableValueOptions.value.map((o) => o.value)));
const treeFingerprint = computed(() =>
  tableValueOptions.value.map((o) => `${o.value}\x1f${o.label}`).join('\x1e')
);

const treeData = computed(() => (Array.isArray(props.tableValuesTree) ? props.tableValuesTree : []));

function stripDisabledForMapTrees(nodes: unknown[]): unknown[] {
  return nodes.map((item) => {
    if (!item || typeof item !== 'object') return item;
    const n = item as Record<string, unknown>;
    const kids = n.children;
    const next: Record<string, unknown> = { ...n, disabled: false };
    if (Array.isArray(kids) && kids.length > 0) {
      next.children = stripDisabledForMapTrees(kids as unknown[]);
    }
    return next;
  });
}

const treeDataForMaps = computed(() => stripDisabledForMapTrees(treeData.value as unknown[]));

const DEFAULT_MAP_METRIC_ID = 'Expected Next 12 Months GWP';

function resolveDefaultMapMetricId(values: Set<string>): string | null {
  if (values.has(DEFAULT_MAP_METRIC_ID)) return DEFAULT_MAP_METRIC_ID;
  const opt = tableValueOptions.value.find(
    (o) =>
      values.has(o.value) &&
      /expected\s+next\s+12/i.test(o.label) &&
      /gwp/i.test(o.label)
  );
  return opt?.value ?? null;
}

type MapsTreeNode = {
  id?: unknown;
  label?: unknown;
  children?: unknown[];
};

function findLeafPathSegments(metricId: string, nodes: unknown[]): string[] | null {
  for (const raw of nodes) {
    if (!raw || typeof raw !== 'object') continue;
    const n = raw as MapsTreeNode;
    const labelRaw = n.label;
    const seg =
      typeof labelRaw === 'string' && labelRaw.trim().length > 0
        ? labelRaw.trim()
        : typeof n.id === 'string'
          ? n.id
          : '';
    const kids = Array.isArray(n.children) ? n.children : [];

    if (kids.length === 0) {
      if (typeof n.id === 'string' && n.id === metricId) {
        return [seg || metricId];
      }
      continue;
    }

    const sub = findLeafPathSegments(metricId, kids);
    if (sub) {
      if (seg) return [seg, ...sub];
      return sub;
    }
  }
  return null;
}

let rootL: am5.Root | null = null;
let rootR: am5.Root | null = null;
const mapChartL = shallowRef<am5map.MapChart | null>(null);
const mapChartR = shallowRef<am5map.MapChart | null>(null);

function metricLabelFor(metricId: string): string {
  if (!metricId) {
    return '—';
  }
  const path = findLeafPathSegments(metricId, treeDataForMaps.value as unknown[]);
  if (path && path.length > 0) {
    const parts = path.filter((s) => !/^table\s+values$/i.test(s.trim()));
    if (parts.length === 0) {
      const opt = tableValueOptions.value.find((o) => o.value === metricId);
      return opt?.label ?? metricId;
    }
    if (parts.length === 1) {
      return parts[0]!;
    }
    return `${parts[0]} - ${parts.slice(1).join(' ')}`;
  }
  const opt = tableValueOptions.value.find((o) => o.value === metricId);
  return opt?.label ?? metricId;
}

const leftMetricLabel = computed(() => metricLabelFor(leftMetric.value));
const rightMetricLabel = computed(() => metricLabelFor(rightMetric.value));

function zoomMapToDefaultBounds(chart: am5map.MapChart, durationMs: number) {
  chart.zoomToGeoBounds(
    {
      left: UK_MAP_VIEW_GEO_BOUNDS.left,
      right: UK_MAP_VIEW_GEO_BOUNDS.right,
      top: UK_MAP_VIEW_GEO_BOUNDS.top,
      bottom: UK_MAP_VIEW_GEO_BOUNDS.bottom,
    },
    durationMs
  );
}

function resetMapView(which: 'L' | 'R') {
  const chart = which === 'L' ? mapChartL.value : mapChartR.value;
  if (!chart) {
    return;
  }
  zoomMapToDefaultBounds(chart, 450);
}

function syncMetricDefaults() {
  const opts = tableValueOptions.value;
  if (!opts.length) {
    leftMetric.value = '';
    rightMetric.value = '';
    return;
  }
  const values = leafValueSet.value;
  if (leftMetric.value && !values.has(leftMetric.value)) {
    leftMetric.value = '';
  }
  if (rightMetric.value && !values.has(rightMetric.value)) {
    rightMetric.value = '';
  }
  const preferred = resolveDefaultMapMetricId(values);
  if (!leftMetric.value && !rightMetric.value && preferred) {
    leftMetric.value = preferred;
    rightMetric.value = preferred;
  }
}

function syncTreeCheckedKeys() {
  nextTick(() => {
    if (leftMetric.value) {
      treeLeftRef.value?.setCheckedKeys([leftMetric.value], false);
    } else {
      treeLeftRef.value?.setCheckedKeys([], false);
    }
    if (rightMetric.value) {
      treeRightRef.value?.setCheckedKeys([rightMetric.value], false);
    } else {
      treeRightRef.value?.setCheckedKeys([], false);
    }
  });
}

function customNodeClass(data: Record<string, unknown>) {
  if (data.isTitle) {
    return 'is-title';
  }
  return 'is-item ' + (data.grayed ? 'text-gray-400' : '');
}

function applyLeftSelect(rawId: string | number) {
  const id = String(rawId);
  if (!leafValueSet.value.has(id)) return;
  leftMetric.value = id;
  nextTick(() => treeLeftRef.value?.setCheckedKeys([id], false));
}

function applyRightSelect(rawId: string | number) {
  const id = String(rawId);
  if (!leafValueSet.value.has(id)) return;
  rightMetric.value = id;
  nextTick(() => treeRightRef.value?.setCheckedKeys([id], false));
}

function renderLeftContent(
  renderH: typeof import('vue').h,
  { node, data }: { node: { label: string }; data: Record<string, unknown> }
) {
  if (!data.isTitle && !data.isSubTitle && !data.disabled) {
    return renderH(
      'div',
      {
        onClick: () => applyLeftSelect(data.id as string | number),
      },
      renderH('span', null, node.label)
    );
  }
  return renderH('div', null, renderH('span', null, node.label));
}

function renderRightContent(
  renderH: typeof import('vue').h,
  { node, data }: { node: { label: string }; data: Record<string, unknown> }
) {
  if (!data.isTitle && !data.isSubTitle && !data.disabled) {
    return renderH(
      'div',
      {
        onClick: () => applyRightSelect(data.id as string | number),
      },
      renderH('span', null, node.label)
    );
  }
  return renderH('div', null, renderH('span', null, node.label));
}

function onLeftTreeCheck(data: Record<string, unknown>, { checkedKeys }: { checkedKeys: string[] }) {
  if (data.isTitle || data.isSubTitle) return;
  const id = data.id != null ? String(data.id) : '';
  if (!id || !leafValueSet.value.has(id)) return;
  if (!checkedKeys.includes(id)) {
    leftMetric.value = '';
    nextTick(() => {
      treeLeftRef.value?.setCheckedKeys([], false);
    });
    return;
  }
  leftMetric.value = id;
  nextTick(() => {
    const cur = treeLeftRef.value?.getCheckedKeys(false) as string[];
    if (cur && cur.length > 1) {
      treeLeftRef.value?.setCheckedKeys([id], false);
    }
  });
}

function onRightTreeCheck(data: Record<string, unknown>, { checkedKeys }: { checkedKeys: string[] }) {
  if (data.isTitle || data.isSubTitle) return;
  const id = data.id != null ? String(data.id) : '';
  if (!id || !leafValueSet.value.has(id)) return;
  if (!checkedKeys.includes(id)) {
    rightMetric.value = '';
    nextTick(() => {
      treeRightRef.value?.setCheckedKeys([], false);
    });
    return;
  }
  rightMetric.value = id;
  nextTick(() => {
    const cur = treeRightRef.value?.getCheckedKeys(false) as string[];
    if (cur && cur.length > 1) {
      treeRightRef.value?.setCheckedKeys([id], false);
    }
  });
}

function disposeMap(which: 'L' | 'R') {
  if (which === 'L' && rootL) {
    mapChartL.value = null;
    rootL.dispose();
    rootL = null;
  }
  if (which === 'R' && rootR) {
    mapChartR.value = null;
    rootR.dispose();
    rootR = null;
  }
}

function heatColors(gwp: boolean): { min: am5.Color; max: am5.Color } {
  if (gwp) {
    return { min: am5.color(0xe8f5e9), max: am5.color(0x1b5e20) };
  }
  return { min: am5.color(0xffebee), max: am5.color(0xb71c1c) };
}

function formatMapMetricForTooltip(value: number, column: string): string {
  if (!Number.isFinite(value)) return '—';
  if (isGwpColumn(column)) {
    return String(numberWithCommas(value, false));
  }
  return String(decimalToPercentage(value, false));
}

function createMap(el: HTMLDivElement | null, column: string, which: 'L' | 'R'): void {
  if (!column) {
    disposeMap(which);
    return;
  }
  if (!el) return;

  disposeMap(which);
  const root = am5.Root.new(el);
  root.setThemes([am5themes_Animated.new(root)]);

  const chart = root.container.children.push(
    am5map.MapChart.new(root, {
      panX: 'translateX',
      panY: 'translateY',
      projection: am5map.geoMercator(),
    })
  ) as am5map.MapChart;

  if (which === 'L') {
    rootL = root;
    mapChartL.value = chart;
  } else {
    rootR = root;
    mapChartR.value = chart;
  }

  const polygonSeries = chart.series.push(
    am5map.MapPolygonSeries.new(root, {
      geoJSON: UK_LOW_GEO_WITHOUT_REPUBLIC_OF_IRELAND as any,
      valueField: 'value',
      calculateAggregates: true,
    })
  );

  polygonSeries.mapPolygons.template.setAll({
    tooltipText: '{name}',
    interactive: true,
    stroke: am5.color(0x9e9e9e),
    strokeWidth: 0.4,
  });

  polygonSeries.mapPolygons.template.adapters.add('tooltipText', (_text, target) => {
    const ctx = target.dataItem?.dataContext as { name?: string; value?: number } | undefined;
    if (!ctx) return '';
    const name = ctx.name != null ? String(ctx.name) : '';
    const v = ctx.value;
    if (v === undefined || v === null || !Number.isFinite(Number(v))) {
      return name ? `${name}: —` : '';
    }
    const formatted = formatMapMetricForTooltip(Number(v), column);
    return name ? `${name}: ${formatted}` : formatted;
  });

  const { min, max } = heatColors(isGwpColumn(column));
  polygonSeries.set('heatRules', [
    {
      target: polygonSeries.mapPolygons.template,
      dataField: 'value',
      min,
      max,
      key: 'fill',
    },
  ]);

  const data = buildAggregatedMapData(props.rows ?? null, column);
  polygonSeries.data.setAll(data);
  root.events.once('frameended', () => {
    zoomMapToDefaultBounds(chart, 0);
  });
}

function redrawBoth() {
  nextTick(() => {
    createMap(leftMapEl.value, leftMetric.value, 'L');
    createMap(rightMapEl.value, rightMetric.value, 'R');
  });
}

watch(
  [
    treeFingerprint,
    leftMetric,
    rightMetric,
    () => dashboard_store.selectedChunkGroup,
    () => portfolio_store.selectedBounceID,
  ],
  async () => {
    syncMetricDefaults();
    await dashboard_store.fetchRegionMapsChunk([leftMetric.value, rightMetric.value]);
    await nextTick();
    await nextTick();
    syncTreeCheckedKeys();
    await nextTick();
    redrawBoth();
  },
  { immediate: true }
);

watch(
  () => props.rows,
  () => {
    redrawBoth();
  }
);

onBeforeUnmount(() => {
  disposeMap('L');
  disposeMap('R');
});
</script>

<template>
  <div class="comparison-region-maps w-full border-t border-gray-200 pt-4 mt-5">
    <h3 class="text-base font-semibold text-gray-800 mb-3 px-2">UK regional heatmaps</h3>
    <div class="comparison-region-maps-row flex flex-col lg:flex-row gap-4 px-2 pb-4">
      <div class="flex-1 min-w-0 rounded border border-gray-100 shadow-sm bg-white p-3 map-pane">
        <div class="mb-2">
          <div v-if="treeDataForMaps.length" class="comparison-region-maps-tree-wrap max-h-64 overflow-y-auto">
            <el-tree
              ref="treeLeftRef"
              :data="treeDataForMaps"
              show-checkbox
              check-strictly
              node-key="id"
              class="filter-tree"
              :render-content="renderLeftContent"
              :props="{ class: customNodeClass, disabled: 'disabled' }"
              @check="onLeftTreeCheck"
            />
          </div>
          <p v-else class="text-xs text-gray-500 py-2">Table values are not loaded yet.</p>
        </div>
        <div class="map-metric-toolbar mb-2">
          <div class="text-sm text-gray-900 font-medium min-w-0">
            <span class="truncate inline-block max-w-full align-bottom" :title="leftMetricLabel">{{
              leftMetricLabel
            }}</span>
          </div>
        </div>
        <div class="comparison-region-map-wrap">
          <div ref="leftMapEl" class="comparison-region-map-canvas" />
          <el-button
            class="map-reset-fab"
            circle
            size="small"
            type="default"
            :icon="Minus"
            title="Reset map view"
            :disabled="!mapChartL"
            @click="resetMapView('L')"
          />
        </div>
      </div>
      <div class="flex-1 min-w-0 rounded border border-gray-100 shadow-sm bg-white p-3 map-pane">
        <div class="mb-2">
          <div v-if="treeDataForMaps.length" class="comparison-region-maps-tree-wrap max-h-64 overflow-y-auto">
            <el-tree
              ref="treeRightRef"
              :data="treeDataForMaps"
              show-checkbox
              check-strictly
              node-key="id"
              class="filter-tree"
              :render-content="renderRightContent"
              :props="{ class: customNodeClass, disabled: 'disabled' }"
              @check="onRightTreeCheck"
            />
          </div>
          <p v-else class="text-xs text-gray-500 py-2">Table values are not loaded yet.</p>
        </div>
        <div class="map-metric-toolbar mb-2">
          <div class="text-sm text-gray-900 font-medium min-w-0">
            <span class="truncate inline-block max-w-full align-bottom" :title="rightMetricLabel">{{
              rightMetricLabel
            }}</span>
          </div>
        </div>
        <div class="comparison-region-map-wrap">
          <div ref="rightMapEl" class="comparison-region-map-canvas" />
          <el-button
            class="map-reset-fab"
            circle
            size="small"
            type="default"
            :icon="Minus"
            title="Reset map view"
            :disabled="!mapChartR"
            @click="resetMapView('R')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.comparison-region-maps-row {
  isolation: isolate;
}

.map-pane {
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.comparison-region-maps-tree-wrap {
  border: 1px solid rgb(243 244 246);
  border-radius: 0.25rem;
}

.comparison-region-maps-tree-wrap
  :deep(.el-tree-node__content:has(.el-tree-node__expand-icon:not(.is-leaf)) .el-checkbox) {
  display: none !important;
  width: 0 !important;
  margin-right: 0 !important;
  margin-inline-end: 0 !important;
  overflow: hidden !important;
  pointer-events: none !important;
}

.comparison-region-map-wrap {
  position: relative;
}

.comparison-region-map-canvas {
  width: 100%;
  height: 380px;
  position: relative;
  overflow: hidden;
}

.map-reset-fab {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 3;
  box-shadow: 0 1px 4px rgb(0 0 0 / 0.12);
}

.comparison-region-map-canvas :deep(a[href*='amcharts.com']),
.comparison-region-map-canvas :deep(a[href*='amcharts']) {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
}
</style>

<style>
.comparison-region-maps .is-title > .el-tree-node__content {
  --tw-text-opacity: 1;
  color: rgb(48 53 60 / var(--tw-bg-opacity));

  height: 30px;

  --tw-bg-opacity: 1;
  background-color: rgb(243 244 246 / var(--tw-bg-opacity));

  padding-top: 5px;
  padding-bottom: 5px;
  --tw-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --tw-shadow-colored: 0 4px 6px -1px var(--tw-shadow-color), 0 2px 4px -2px var(--tw-shadow-color);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;

  position: sticky;
  top: 0;
  font-size: 18px;
  z-index: 2;
}
</style>
